import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { fetchWithAuth } from "./fetchWithAuth";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    let errorText = res.statusText;
    try {
      const text = await res.text();
      if (text) {
        try {
          const json = JSON.parse(text);
          errorText = json.message || text;
        } catch (e) {
          errorText = text;
        }
      }
    } catch (e) {
      console.error("Error reading response text:", e);
    }
    throw new Error(`${res.status}: ${errorText}`);
  }
}

export async function apiRequest<T = any>(
  urlOrOptions: string | { method: string; url: string; body?: string; data?: any },
  options?: { method: string; body?: string; data?: any }
): Promise<T> {
  let url: string;
  let method: string;
  let body: string | undefined;
  let data: any | undefined;

  if (typeof urlOrOptions === 'string') {
    url = urlOrOptions;
    method = options?.method || 'GET';
    data = options?.data; 
    body = options?.body;
  } else {
    url = urlOrOptions.url;
    method = urlOrOptions.method;
    data = urlOrOptions.data;
    body = urlOrOptions.body;
  }

  if (data !== undefined && body === undefined) {
    body = JSON.stringify(data);
  }

  // CRITICAL: Always use the same exact origin as the current page for session cookies
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  // Use fetchWithAuth to automatically attach Auth0 Bearer token
  const res = await fetchWithAuth(fullUrl, {
    method,
    body: body,
  });

  await throwIfResNotOk(res);

  if (res.status === 204) {
    return {} as T;
  }

  return await res.json() as T;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: async ({ queryKey }) => {
        const [url, params] = queryKey;
        let finalUrl = url as string;

        if (params) {
          if (typeof params === 'object') {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                queryParams.append(key, value.toString());
              }
            });
            const queryString = queryParams.toString();
            if (queryString) {
              finalUrl = `${finalUrl}?${queryString}`;
            }
          } else {
            finalUrl = `${finalUrl}/${params}`;
          }
        }

        return apiRequest(finalUrl);
      },
      // Standard settings that allow authentication queries
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnMount: true, // Allow queries to run on mount
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: false,
      enabled: true, // Enable queries by default - individual queries can override this
    },
    mutations: {
      retry: false,
    },
  },
});